import { PlaceOrderDtoSide, PlaeePositionResponseDtoSide } from '@/lib/models';
import type { BuyTargetDto, MarketOptionDto } from '@/lib/models';

/**
 * Contract side to SELL in order to reduce/close a netted position.
 *
 * A LONG position is net-long the YES contract (close by selling `yes`); a SHORT
 * position closes by selling `no`. The netted position DTO carries only
 * LONG/SHORT, so this derives the contract side the sell order needs.
 * (Confirmed with Plaee 2026-06-21.)
 */
export function closeSide(side: PlaeePositionResponseDtoSide): PlaceOrderDtoSide {
  return side === PlaeePositionResponseDtoSide.LONG ? PlaceOrderDtoSide.yes : PlaceOrderDtoSide.no;
}

/** A netted position resolved to the option + contract it represents. */
export interface ResolvedPositionLeg {
  /** The market option (binary market, or one MULTI candidate) the position belongs to. */
  option: MarketOptionDto;
  /** The bet the position represents — its Yes/No identity. */
  betSide: 'yes' | 'no';
  /** The buy target that prices the held contract (drives live price, value, P&L). */
  target: BuyTargetDto;
}

/**
 * Map a netted position to the option + side it represents — generically, for
 * every market shape, from a single rule:
 *
 * - **2-outcome binary** — Yes and No are DISTINCT outcomes, so the bet's
 *   identity comes from *which outcome leg* is held: long the No-outcome is a
 *   "No" bet, long the Yes-outcome is a "Yes" bet (a net short flips it).
 * - **1-outcome binary / MULTI candidate** — Yes and No are the two sides of
 *   ONE outcome (same `externalOutcomeId`), so the netted direction decides:
 *   LONG = Yes, SHORT = No.
 *
 * Both collapse to: pick the leg whose `externalOutcomeId` matches; if that
 * match is the *distinct* No-leg, LONG means "No"; otherwise LONG means "Yes".
 *
 * Returns `null` when the position isn't on any of the supplied options.
 */
export function resolvePositionLeg(
  externalOutcomeId: string,
  side: PlaeePositionResponseDtoSide,
  options: MarketOptionDto[],
): ResolvedPositionLeg | null {
  const long = side === PlaeePositionResponseDtoSide.LONG;
  for (const option of options) {
    const onYes = externalOutcomeId === option.yes.externalOutcomeId;
    const onNo = externalOutcomeId === option.no.externalOutcomeId;
    if (!onYes && !onNo) continue;
    const distinctNoLeg = onNo && !onYes;
    const betSide: 'yes' | 'no' = distinctNoLeg ? (long ? 'no' : 'yes') : long ? 'yes' : 'no';
    return { option, betSide, target: betSide === 'yes' ? option.yes : option.no };
  }
  return null;
}
